import { inject } from '@angular/core'
import { toObservable } from '@angular/core/rxjs-interop'
import { NavigationEnd, Router } from '@angular/router'
import {
  patchState,
  signalStore,
  withFeature,
  withHooks,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals'
import { withEventHandlers } from '@ngrx/signals/events'
import { setUser } from '@sentry/angular'
import { combineLatest, Observable, Subject, Subscription } from 'rxjs'
import { distinctUntilChanged, map, tap } from 'rxjs/operators'
import { BroadcastChannelGroup } from '../../broadcasts/enums/broadcast-channel-group'
import { BroadcastChannelVisibility } from '../../broadcasts/enums/broadcast-channel-visibility'
import { BroadcastEvent } from '../../broadcasts/enums/broadcast-event'
import { withBroadcasts } from '../../broadcasts/stores/features/with-broadcasts'
import { BroadcastChannel } from '../../broadcasts/utilities/broadcast-channel'
import { AnonymousData } from '../../rest/decorators/mapped'
import { FractalService } from '../../rest/services/fractal.service'
import { setErrors, withErrors } from '../../rest/stores/features/with-errors'
import { withLoading } from '../../rest/stores/features/with-loading'
import { Notification } from '../models/notification'
import { User } from '../models/user'
import { AuthService } from '../services/auth.service'
import { WINDOW } from '../tokens/window'
import { ManagerVersion } from '../types/manager-version'
import { subscribeToRequest } from '../utilities/subscribe-to-request'

import { Message } from '../models/message'
import { MessagingService } from '../services/messaging.service'
import { AuthenticationResult } from '../types/authentication-result'
import { ReturnURL } from '../types/return-url'

interface State {
  user: User | null | undefined
  loggingIn: boolean
  verifyingCode: boolean
  codeRequired: 'email' | 'authenticator' | null
  verificationID: string | null
  requestingReset: boolean
  resetRequested: boolean
  resettingPassword: boolean
  passwordReset: boolean
  fetchingUser: boolean
  fetchingAuthenticatorURL: boolean
  completingAuthenticatorConfiguration: boolean
  redirecting: boolean
  verifyingSocialUser: boolean
  loggingOut: boolean
  managerVersion: ManagerVersion | null
}

const initialState: State = {
  user: undefined,
  loggingIn: false,
  verifyingCode: false,
  codeRequired: null,
  verificationID: null,
  requestingReset: false,
  resetRequested: false,
  resettingPassword: false,
  passwordReset: false,
  fetchingUser: false,
  fetchingAuthenticatorURL: false,
  completingAuthenticatorConfiguration: false,
  redirecting: false,
  verifyingSocialUser: false,
  loggingOut: false,
  managerVersion: null,
}

export const AuthStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withProps(
    (
      store
    ): {
      notifications$: Subject<Notification>
      observeUser$: Observable<User | null | undefined>
      observeAuthenticating$: Observable<boolean>[]
      requestSubscription: Subscription | null
    } => ({
      notifications$: new Subject<Notification>(),
      observeUser$: toObservable(store.user),
      observeAuthenticating$: [
        toObservable(store.loggingIn),
        toObservable(store.verifyingCode),
        toObservable(store.fetchingUser),
        toObservable(store.verifyingSocialUser),
        toObservable(store.loggingOut),
      ],
      requestSubscription: null,
    })
  ),
  withBroadcasts(),
  withErrors(),
  withHooks({
    onInit(store, authService = inject(AuthService)) {
      authService
        .getUser()
        .then(({ user, version }) =>
          patchState(store, { user, managerVersion: version })
        )
        .catch(() => patchState(store, { user: null }))
    },
  }),
  withEventHandlers(
    (store, router = inject(Router), fractal = inject(FractalService)) => ({
      routeChanges$: router.events.pipe(
        tap((event) => {
          if (event instanceof NavigationEnd) {
            patchState(store, setErrors(null))
          }
        })
      ),
      user$: toObservable(store.user).pipe(
        tap((user) => {
          if (user) {
            setUser({
              id: user.numericID,
              email: user.email,
              username: user.name,
            })
            store.subscribeToChannel(
              new BroadcastChannel(
                BroadcastChannelGroup.user,
                BroadcastChannelVisibility.private,
                [{ key: 'ulid', value: user.ulid }]
              ),
              [
                {
                  event: BroadcastEvent.userUpdated,
                  process: (data: AnonymousData) => {
                    patchState(store, {
                      user: fractal.deserialiseItem<AnonymousData, User>(
                        data,
                        User.create
                      ),
                    })
                  },
                },
                {
                  event: BroadcastEvent.notificationUpdated,
                  process: (data: AnonymousData) =>
                    store.notifications$.next(
                      fractal.deserialiseItem<AnonymousData, Notification>(
                        data,
                        Notification.create
                      )
                    ),
                },
              ]
            )
          }
        })
      ),
    })
  ),
  withMethods(
    (
      store,
      authService = inject(AuthService),
      messaging = inject(MessagingService),
      router = inject(Router),
      window = inject(WINDOW)
    ) => ({
      waitForUser() {
        return new Promise<User | null>((resolve) => {
          combineLatest([store.observeUser$, ...store.observeAuthenticating$])
            .pipe(distinctUntilChanged())
            .pipe(map(([user, ...loading]) => ({ user, loading })))
            .subscribe(({ user, loading }) => {
              if (
                user !== undefined &&
                loading.filter((loading) => loading).length === 0
              ) {
                resolve(user)
              }
            })
        })
      },
      async logIn(email: string, password: string, redirect: ReturnURL) {
        patchState(store, { loggingIn: true, ...setErrors(null) })
        const result = await subscribeToRequest(
          await authService.logIn(email, password),
          store.requestSubscription,
          (subscription) => (store.requestSubscription = subscription)
        ).catch((errors) => {
          patchState(store, { loggingIn: false, ...setErrors(errors) })
          return null
        })
        return this._handleAuthenticationResult(result, redirect)
      },
      async verifyCode(
        email: string,
        password: string,
        code: string,
        redirect: ReturnURL
      ) {
        const method = store.codeRequired()
        if (!method) {
          return
        }
        patchState(store, { verifyingCode: true, ...setErrors(null) })
        const result = await subscribeToRequest(
          await authService.verifyCode(
            method,
            email,
            password,
            code,
            store.verificationID()
          ),
          store.requestSubscription,
          (subscription) => (store.requestSubscription = subscription)
        ).catch((errors) => {
          patchState(store, { verifyingCode: false, ...setErrors(errors) })
          return null
        })
        return this._handleAuthenticationResult(result, redirect)
      },
      async lostPassword(email: string) {
        patchState(store, { requestingReset: true, ...setErrors(null) })
        await subscribeToRequest(
          await authService.lostPassword(
            email,
            `${window?.location.origin}/set-password`
          ),
          store.requestSubscription,
          (subscription) => (store.requestSubscription = subscription)
        ).catch(() => null)
        patchState(store, { requestingReset: false, resetRequested: true })
      },
      async resetPassword(
        email: string,
        password: string,
        token: string,
        redirect: ReturnURL
      ) {
        patchState(store, { resettingPassword: true, ...setErrors(null) })
        const result = await subscribeToRequest(
          await authService.resetPassword(email, password, token),
          store.requestSubscription,
          (subscription) => (store.requestSubscription = subscription)
        )
          .then((result) => {
            patchState(store, { passwordReset: true })
            return result
          })
          .catch((errors) => {
            patchState(store, {
              resettingPassword: false,
              ...setErrors(errors),
            })
            return null
          })
        return this._handleAuthenticationResult(result, redirect)
      },
      async authenticatorURL() {
        patchState(store, {
          fetchingAuthenticatorURL: true,
          ...setErrors(null),
        })
        return subscribeToRequest(
          await authService.authenticatorURL(),
          store.requestSubscription,
          (subscription) => (store.requestSubscription = subscription)
        )
          .then((url) => {
            patchState(store, { fetchingAuthenticatorURL: false })
            return url
          })
          .catch((errors) => {
            patchState(store, {
              fetchingAuthenticatorURL: false,
              ...setErrors(errors),
            })
            return null
          })
      },
      async completeAuthenticatorConfiguration(code: string) {
        patchState(store, {
          completingAuthenticatorConfiguration: true,
          ...setErrors(null),
        })
        return subscribeToRequest(
          await authService.completeAuthenticatorConfiguration(code),
          store.requestSubscription,
          (subscription) => (store.requestSubscription = subscription)
        )
          .then((user) => {
            patchState(store, {
              completingAuthenticatorConfiguration: false,
              user,
            })
            messaging.broadcast(
              new Message(
                'authenticator-configured',
                'Authenticator App Configured',
                'Two-factor authentication is now set-up on your account',
                Message.Good
              )
            )
          })
          .catch((errors) => {
            patchState(store, {
              completingAuthenticatorConfiguration: false,
              ...setErrors(errors),
            })
          })
      },
      async redirectToSocialLogin() {
        patchState(store, { redirecting: true, ...setErrors(null) })
        return subscribeToRequest(
          await authService.getSocialUrl(),
          store.requestSubscription,
          (subscription) => (store.requestSubscription = subscription)
        )
          .then((url) => {
            if (window && url) {
              window.location.href = url
            }
          })
          .catch((error) => {
            patchState(store, {
              redirecting: false,
              ...setErrors(error),
            })
          })
      },
      async verifySocialLogin(
        code: string,
        state: string,
        redirect: ReturnURL
      ) {
        patchState(store, { verifyingSocialUser: true, ...setErrors(null) })
        const result = await subscribeToRequest(
          await authService.verifySocial(code, state),
          store.requestSubscription,
          (subscription) => (store.requestSubscription = subscription)
        ).catch((error) => {
          patchState(store, { verifyingSocialUser: false, ...setErrors(error) })
          return null
        })
        return this._handleAuthenticationResult(result, redirect)
      },
      async reset() {
        authService.reset()
        await router
          .navigate(['/log-in'])
          .catch(() => window?.location.reload())
      },
      async logOut() {
        patchState(store, { loggingOut: true, ...setErrors(null) })
        const loggedOut = await subscribeToRequest(
          await authService.logOut(),
          store.requestSubscription,
          (subscription) => (store.requestSubscription = subscription)
        ).catch((errors) => {
          patchState(store, {
            loggingOut: false,
            ...setErrors(errors),
          })
          return false
        })
        if (loggedOut) {
          patchState(store, { loggingOut: false, user: null })
          await this.reset()
        }
        return loggedOut
      },
      async _handleAuthenticationResult(
        result: AuthenticationResult | null,
        redirect: ReturnURL
      ) {
        if (result) {
          if (result.method) {
            patchState(store, {
              loggingIn: false,
              verifyingCode: false,
              resettingPassword: false,
              codeRequired: result.method,
              verificationID: result.verification ?? null,
            })
          } else {
            patchState(store, {
              loggingIn: false,
              verifyingCode: false,
              resettingPassword: false,
              fetchingUser: true,
            })
            return authService
              .getUser()
              .then(({ user, version }) => {
                patchState(store, {
                  user,
                  managerVersion: version,
                  fetchingUser: false,
                  codeRequired: null,
                  verificationID: null,
                })
                return router.navigate(
                  redirect.segments,
                  redirect.queryParams
                    ? { queryParams: redirect.queryParams }
                    : undefined
                )
              })
              .catch((errors) => {
                patchState(store, { fetchingUser: false, ...setErrors(errors) })
              })
          }
        }
      },
    })
  ),
  withFeature((store) =>
    withLoading([
      toObservable(store.loggingIn),
      toObservable(store.verifyingCode),
      toObservable(store.fetchingUser),
      toObservable(store.requestingReset),
      toObservable(store.resettingPassword),
      toObservable(store.verifyingSocialUser),
      toObservable(store.fetchingAuthenticatorURL),
      toObservable(store.completingAuthenticatorConfiguration),
      toObservable(store.redirecting),
      toObservable(store.loggingOut),
    ])
  )
)
